import sentry from 'utils/sentry/sentry'

// pusher-js's maximum connect-attempt timeout (`timeoutLimit` in runtimes/web/default_strategy.ts); recheck on upgrade.
const PUSHER_CONNECT_ATTEMPT_LIMIT_MS = 60_000
export const OUTAGE_ALERT_AFTER_MS = 5 * PUSHER_CONNECT_ATTEMPT_LIMIT_MS
export const OUTAGE_TICK_MS = 10_000
const CLOCK_SUSPENSION_GAP_MS = 3 * OUTAGE_TICK_MS

type ConnectionOutage = {
  state: string
  observedOutageMs: number
  lastError?: { error: unknown; ageMs: number }
}

type WatchedConnectionEvent = 'error' | 'connected'

type WatchedConnection = {
  readonly state: string
  bind(eventName: WatchedConnectionEvent, callback: (payload: unknown) => void): unknown
  unbind(eventName: WatchedConnectionEvent, callback: (payload: unknown) => void): unknown
}

function toMinutes(ms: number): number {
  return Math.round(ms / 60_000)
}

function describeAge(ms: number): string {
  return ms < 60_000 ? `${Math.round(ms / 1000)} s` : `${toMinutes(ms)} min`
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function toJsonOrTypeName(value: unknown): string {
  try {
    return JSON.stringify(value) ?? typeof value
  } catch {
    return typeof value
  }
}

function describeErrorData(data: unknown): string {
  if (typeof data === 'string') {
    return data ? `: ${data}` : ''
  }
  if (!isRecord(data)) {
    return ''
  }

  const { code, message } = data
  if (code === undefined && message === undefined) {
    return `: ${toJsonOrTypeName(data)}`
  }
  const codeText = typeof code === 'number' || typeof code === 'string' ? ` ${code}` : ''
  const messageText = typeof message === 'string' && message ? `: ${message}` : ''
  return `${codeText}${messageText}`
}

export function describeConnectionError(error: unknown): string {
  if (error instanceof Error) {
    return `${error.name}: ${error.message}`
  }
  if (isRecord(error) && typeof error.type === 'string') {
    if (isRecord(error.error)) {
      return `${error.type}: ${describeConnectionError(error.error)}`
    }
    return `${error.type}${describeErrorData(error.data)}`
  }
  return toJsonOrTypeName(error)
}

export function watchConnectionOutage(
  connection: WatchedConnection,
  report: (outage: ConnectionOutage) => void
): () => void {
  let previousTickAt = Date.now()
  let observedOutageMs = 0
  let hasReported = false
  let lastError: { error: unknown; recordedAt: number } | undefined

  const handleError = (error: unknown): void => {
    lastError = { error, recordedAt: Date.now() }
  }

  const handleConnected = (): void => {
    observedOutageMs = 0
    hasReported = false
    lastError = undefined
  }

  const tick = (): void => {
    const now = Date.now()
    const sincePreviousTick = now - previousTickAt
    previousTickAt = now

    if (connection.state === 'connected') {
      return
    }
    if (sincePreviousTick > CLOCK_SUSPENSION_GAP_MS) {
      observedOutageMs = 0
      return
    }
    if (document.visibilityState !== 'visible' || navigator.onLine === false) {
      return
    }

    observedOutageMs += sincePreviousTick
    if (observedOutageMs >= OUTAGE_ALERT_AFTER_MS && !hasReported) {
      hasReported = true
      report({
        state: connection.state,
        observedOutageMs,
        lastError: lastError && { error: lastError.error, ageMs: now - lastError.recordedAt },
      })
    }
  }

  connection.bind('error', handleError)
  connection.bind('connected', handleConnected)
  const interval = setInterval(tick, OUTAGE_TICK_MS)

  return () => {
    clearInterval(interval)
    connection.unbind('error', handleError)
    connection.unbind('connected', handleConnected)
  }
}

function describeLastError(lastError: ConnectionOutage['lastError']): string {
  if (!lastError) {
    return 'no error event'
  }
  return `${describeConnectionError(lastError.error)}, ${describeAge(lastError.ageMs)} ago`
}

export function reportConnectionOutage(outage: ConnectionOutage): void {
  sentry.captureScopedMessage({
    scope: 'pusher',
    issueType: 'connection_not_recovered',
    level: 'error',
    message: `Pusher not connected for ${toMinutes(outage.observedOutageMs)} min of visible, online time (state: ${outage.state}; last error: ${describeLastError(outage.lastError)})`,
    tags: { pusher_state: outage.state },
  })
}
